import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AdminKeyService } from '../../services/adminKeyService';
import { SubPayOption, SubPayOptionsGroup, SubPayOptionsService } from '../../services/subPayOptionsService';

const newOption = (): SubPayOption => ({
  id: `opt_${Date.now()}`,
  title: '',
  description: '',
  price: 0,
  badge: '',
  recommended: false,
  enabled: true,
  action: {
    type: 'visibility_boost',
    label: 'Boosting visibility',
    details: 'Boost visibility for this listing.',
  },
});

const ACTION_PRESETS: Array<{
  type: NonNullable<SubPayOption['action']>['type'];
  label: string;
  details: string;
  duration_days?: number;
}> = [
  {
    type: 'visibility_boost',
    label: 'Boosting visibility',
    details: 'Boosting visibility (generic).',
  },
  {
    type: 'hochschieben_once',
    label: 'Hochschieben (einmalig)',
    details: 'Neue Aufmerksamkeit erhalten! Deine Anzeige sieht aus wie eine neue Anzeige.',
    duration_days: 0,
  },
  {
    type: 'highlight_7d',
    label: 'Highlight (7 Tage)',
    details: 'Bis zu 2x mehr Sichtbarkeit! Deine Anzeige wird farbig hervorgehoben.',
    duration_days: 7,
  },
  {
    type: 'hochschieben_daily_7d',
    label: 'Wiederholtes Hochschieben (7 Tage)',
    details: 'Bis zu 5x mehr Sichtbarkeit! Eine Woche lang wird deine Anzeige jeden Tag nach oben geschoben.',
    duration_days: 7,
  },
  {
    type: 'top_anzeige_7d',
    label: 'Top-Anzeige (7 Tage)',
    details: 'Bis zu 10x mehr Sichtbarkeit! Deine Anzeige steht am Anfang der Liste.',
    duration_days: 7,
  },
  {
    type: 'galerie_startseite_10d',
    label: 'Galerie / Startseite (10 Tage)',
    details: 'Bis zu 15x mehr Sichtbarkeit! Deine Anzeige erscheint zusätzlich auf der Startseite.',
    duration_days: 10,
  },
];

const POPULAR_CURRENCIES: Array<{ code: string; label: string }> = [
  { code: '', label: 'None' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
  { code: 'TRY', label: 'TRY — Turkish Lira' },
  { code: 'AED', label: 'AED — UAE Dirham' },
  { code: 'SAR', label: 'SAR — Saudi Riyal' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
];

const newGroup = (currency: string): SubPayOptionsGroup => ({
  id: `${String(currency || '').toLowerCase()}_${Date.now()}`,
  currency,
  active: false,
  options: [],
});

const BoostVisibilityTab: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [groups, setGroups] = React.useState<SubPayOptionsGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = React.useState<string>('');
  const [newGroupCurrency, setNewGroupCurrency] = React.useState<string>('');
  const [priceDrafts, setPriceDrafts] = React.useState<Record<string, string>>({});
  const [actionDialogOpen, setActionDialogOpen] = React.useState(false);
  const [actionDialogIdx, setActionDialogIdx] = React.useState<number | null>(null);
  const [actionDraft, setActionDraft] = React.useState<NonNullable<SubPayOption['action']> | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const doc = await SubPayOptionsService.getOptions();
      const nextGroups: SubPayOptionsGroup[] = Array.isArray(doc.groups)
        ? doc.groups.map((g) => ({
            id: String(g.id || '').trim(),
            currency: String(g.currency || '').trim(),
            active: !!g.active,
            options: (Array.isArray(g.options) ? g.options : []).map((o) => ({
              ...o,
              enabled: o.enabled !== false,
              action: o?.action ? { ...o.action } : undefined,
            })),
          }))
        : [
            {
              id: `eur_${Date.now()}`,
              currency: String(doc.currency || 'EUR').trim() || 'EUR',
              active: true,
              options: (doc.options || []).map((o) => ({
                ...o,
                enabled: o.enabled !== false,
                action: o?.action ? { ...o.action } : undefined,
              })),
            },
          ];

      // Ensure one active group
      const activeIdx = nextGroups.findIndex((g) => g.active);
      if (activeIdx === -1 && nextGroups.length > 0) nextGroups[0].active = true;
      if (activeIdx > -1) {
        nextGroups.forEach((g, i) => {
          if (i !== activeIdx) g.active = false;
        });
      }

      setGroups(nextGroups);
      const active = nextGroups.find((g) => g.active) || nextGroups[0];
      setSelectedGroupId(active?.id || nextGroups[0]?.id || '');
    } catch (e: any) {
      toast.error(`Failed to load: ${e?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const selectedGroup = React.useMemo(
    () => groups.find((g) => g.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  React.useEffect(() => {
    // Keep drafts in sync when switching group / reloading
    setPriceDrafts((prev) => {
      const next: Record<string, string> = {};
      const opts = selectedGroup?.options || [];
      for (const o of opts) {
        const key = String(o.id || '');
        if (!key) continue;
        next[key] = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : String(Number(o.price) || 0);
      }
      return next;
    });
  }, [selectedGroupId, selectedGroup?.options]);

  const normalizeCurrency = (v: string) => String(v || '').trim().toUpperCase();

  const setSelectedAndActive = (groupId: string) => {
    setSelectedGroupId(groupId);
    setGroups((prev) => prev.map((g) => ({ ...g, active: g.id === groupId })));
  };

  const setGroupCurrency = (groupId: string, rawCurrency: string) => {
    const nextCurrency = normalizeCurrency(rawCurrency);
    setGroups((prev) => {
      const next = prev.map((g) => {
        if (g.id === groupId) return { ...g, currency: nextCurrency };
        // Enforce unique currency: if some other group already has this currency, clear it.
        if (nextCurrency && normalizeCurrency(g.currency) === nextCurrency) {
          return { ...g, currency: '' };
        }
        return g;
      });

      // If active group ended up without currency AND there is any other group with a currency,
      // keep active as-is (user can decide), but ensure selectedGroupId still exists.
      if (!next.some((g) => g.id === selectedGroupId)) {
        const active = next.find((g) => g.active) || next[0];
        setSelectedGroupId(active?.id || '');
      }

      return next;
    });
  };

  const updateOption = (idx: number, patch: Partial<SubPayOption>) => {
    if (!selectedGroup) return;
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== selectedGroup.id) return g;
        return { ...g, options: g.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)) };
      })
    );
  };

  const openActionDialog = (idx: number) => {
    if (!selectedGroup) return;
    const opt = selectedGroup.options[idx];
    const nextAction =
      opt?.action ||
      ({
        type: 'visibility_boost',
        label: 'Boosting visibility',
        details: 'Boost visibility for this listing.',
      } as NonNullable<SubPayOption['action']>);
    setActionDialogIdx(idx);
    setActionDraft({ ...nextAction });
    setActionDialogOpen(true);
  };

  const closeActionDialog = () => {
    setActionDialogOpen(false);
    setActionDialogIdx(null);
    setActionDraft(null);
  };

  const saveActionDialog = () => {
    if (actionDialogIdx == null || !actionDraft) {
      closeActionDialog();
      return;
    }
    updateOption(actionDialogIdx, { action: { ...actionDraft } });
    closeActionDialog();
  };

  const setPriceDraft = (optId: string, draft: string) => {
    setPriceDrafts((prev) => ({ ...prev, [optId]: draft }));
  };

  const commitPriceDraft = (idx: number, optId: string) => {
    const raw = String(priceDrafts[optId] ?? '').trim();
    if (raw === '') {
      setPriceDraft(optId, '0');
      updateOption(idx, { price: 0 });
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setPriceDraft(optId, '0');
      updateOption(idx, { price: 0 });
      return;
    }
    // Keep up to 2 decimals visually if user entered more
    setPriceDraft(optId, String(parsed));
    updateOption(idx, { price: parsed });
  };

  const removeOption = (idx: number) => {
    if (!selectedGroup) return;
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== selectedGroup.id) return g;
        return { ...g, options: g.options.filter((_, i) => i !== idx) };
      })
    );
  };

  const addOption = () => {
    if (!selectedGroup) return;
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== selectedGroup.id) return g;
        return { ...g, options: [...g.options, newOption()] };
      })
    );
  };

  const addGroup = () => {
    const cur = normalizeCurrency(newGroupCurrency);
    setGroups((prev) => {
      const next = [...prev];

      // If currency selected and already exists, just select+activate that set instead of creating duplicates
      if (cur) {
        const existing = next.find((g) => normalizeCurrency(g.currency) === cur);
        if (existing) {
          setSelectedAndActive(existing.id);
          return prev;
        }
      }

      const created = newGroup(cur);
      next.push(created);
      // Make newly created set selected, and active if nothing is active yet
      if (!next.some((g) => g.active)) {
        created.active = true;
      }
      setSelectedGroupId(created.id);
      return next;
    });
  };

  const deleteGroup = (groupId: string) => {
    setGroups((prev) => {
      const next = prev.filter((g) => g.id !== groupId);
      if (next.length === 0) {
        setSelectedGroupId('');
        return [];
      }
      const hasActive = next.some((g) => g.active);
      if (!hasActive) next[0].active = true;

      setSelectedGroupId((prevSelected) => {
        if (prevSelected && prevSelected !== groupId && next.some((g) => g.id === prevSelected)) {
          return prevSelected;
        }
        const active = next.find((g) => g.active) || next[0];
        return active?.id || '';
      });

      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const loadingId = toast.loading('Saving boost options...');
    try {
      let token = await AdminKeyService.getSecret();
      if (!token) {
        toast.error('Missing admin key. Please set it in Website Settings first.', { id: loadingId });
        setSaving(false);
        return;
      }

      const cleanedGroupsAll: SubPayOptionsGroup[] = groups
        .map((g) => {
          const gCurrency = normalizeCurrency(g.currency);
          const gId = String(g.id || '').trim() || `${(gCurrency || 'cur').toLowerCase()}_${Date.now()}`;
          const gOptions: SubPayOption[] = (Array.isArray(g.options) ? g.options : [])
            .map((o) => ({
              id: String(o.id || '').trim(),
              title: String(o.title || '').trim(),
              description: String(o.description || '').trim(),
              price: Number(o.price) || 0,
              badge: String(o.badge || '').trim() || undefined,
              recommended: !!o.recommended,
              enabled: o.enabled !== false,
              action: o?.action
                ? {
                    type: o.action.type,
                    label: String(o.action.label || '').trim() || undefined,
                    details: String(o.action.details || '').trim() || undefined,
                    duration_days:
                      typeof o.action.duration_days === 'number' && Number.isFinite(o.action.duration_days)
                        ? o.action.duration_days
                        : undefined,
                  }
                : undefined,
            }))
            .filter((o) => o.id && o.title && o.description);

          return { id: gId, currency: gCurrency, active: !!g.active, options: gOptions };
        })
        .filter((g) => g.id);

      // Only persist groups that actually have a currency assigned
      const cleanedGroups: SubPayOptionsGroup[] = cleanedGroupsAll.filter((g) => !!normalizeCurrency(g.currency));

      // Ensure one active group
      const activeIdx = cleanedGroups.findIndex((g) => g.active);
      if (activeIdx === -1 && cleanedGroups.length > 0) cleanedGroups[0].active = true;
      if (activeIdx > -1) {
        cleanedGroups.forEach((g, i) => {
          if (i !== activeIdx) g.active = false;
        });
      }

      await SubPayOptionsService.updateGroups({ token, groups: cleanedGroups });
      toast.success('Saved', { id: loadingId });
    } catch (e: any) {
      toast.error(`Save failed: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <AutoAwesomeIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" fontWeight={900}>
                  Boost & Visibility
                </Typography>
                <Chip size="small" label="Pricing options" sx={{ fontWeight: 800 }} />
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={load} disabled={loading || saving}>
                  Reload
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}>
                  Save
                </Button>
              </Stack>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Configure what appears in the job upload upsell step (name, description, price, badge, recommended).
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2, alignItems: 'start' }}>
              <Card sx={{ borderRadius: 3, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack spacing={1.25}>
                    <Typography fontWeight={900}>Currency sets</Typography>

                    <FormControl size="small">
                      <InputLabel id="boost-new-currency-label">New currency</InputLabel>
                      <Select
                        labelId="boost-new-currency-label"
                        label="New currency"
                        value={newGroupCurrency}
                        onChange={(e) => setNewGroupCurrency(String(e.target.value))}
                      >
                        {POPULAR_CURRENCIES.map((c) => (
                          <MenuItem key={c.code || 'none'} value={c.code}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button startIcon={<AddIcon />} variant="contained" onClick={addGroup}>
                      Create currency set
                    </Button>

                    <Divider sx={{ my: 1 }} />

                    <Stack spacing={1}>
                      {groups.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Create a currency set to start.
                        </Typography>
                      )}

                      {groups.map((g) => {
                        const isSelected = g.id === selectedGroupId;
                        const isActive = !!g.active;
                        const currencyLabel = normalizeCurrency(g.currency) || 'None';
                        return (
                          <Card
                            key={g.id}
                            sx={{
                              borderRadius: 2.5,
                              border: '1px solid',
                              borderColor: isSelected ? 'transparent' : 'divider',
                              overflow: 'hidden',
                              ...(isSelected && {
                                backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                backgroundOrigin: 'border-box',
                                backgroundClip: 'padding-box, border-box',
                              }),
                            }}
                          >
                            <CardActionArea onClick={() => setSelectedAndActive(g.id)}>
                              <CardContent sx={{ p: 1.75 }}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                    <Typography fontWeight={900} noWrap>
                                      {currencyLabel}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {g.options?.length || 0} options
                                    </Typography>
                                  </Stack>

                                  <Stack direction="row" spacing={1} alignItems="center">
                                    {isActive && <Chip size="small" label="Active" color="success" sx={{ fontWeight: 800 }} />}
                                    <Button
                                      color="error"
                                      size="small"
                                      variant="outlined"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        deleteGroup(g.id);
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(!selectedGroup || selectedGroup.options.length === 0) && (
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography fontWeight={900}>No options yet</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select a currency set and click “Add option” to create your first boost purchase.
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {selectedGroup && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
                    <Typography fontWeight={900}>
                      Options for <span style={{ color: theme.palette.primary.main }}>{selectedGroup.currency}</span>
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel id="boost-selected-currency-label">Currency</InputLabel>
                      <Select
                        labelId="boost-selected-currency-label"
                        label="Currency"
                        value={selectedGroup.currency}
                        onChange={(e) => setGroupCurrency(selectedGroup.id, String(e.target.value))}
                      >
                        {POPULAR_CURRENCIES.map((c) => (
                          <MenuItem key={c.code || 'none'} value={c.code}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={addOption}>
                      Add option
                    </Button>
                  </Stack>
                )}

                {selectedGroup?.options.map((opt, idx) => (
                  <Card key={opt.id || idx} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={opt.id || 'id'}
                              variant="outlined"
                              sx={{ fontWeight: 800, maxWidth: 240 }}
                            />
                            {opt.recommended && <Chip size="small" color="primary" label="Recommended" sx={{ fontWeight: 800 }} />}
                            {opt.badge ? <Chip size="small" label={opt.badge} sx={{ fontWeight: 800 }} /> : null}
                            {selectedGroup?.active && opt.enabled !== false ? (
                              <Chip size="small" color="success" label="Live" sx={{ fontWeight: 800 }} />
                            ) : null}
                          </Stack>

                          <Stack direction="row" spacing={1}>
                            <Button
                              color="error"
                              variant="outlined"
                              startIcon={<DeleteIcon />}
                              onClick={() => removeOption(idx)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                          <TextField
                            label="ID (unique)"
                            value={opt.id}
                            onChange={(e) => updateOption(idx, { id: e.target.value })}
                            helperText="Example: featured, highlight, homepage"
                          />
                          <TextField
                            label={`Price (${selectedGroup?.currency || 'CUR'})`}
                            type="number"
                            value={priceDrafts[String(opt.id || '')] ?? String(Number(opt.price) || 0)}
                            onChange={(e) => {
                              const id = String(opt.id || '');
                              if (!id) return;
                              setPriceDraft(id, e.target.value);
                            }}
                            onBlur={() => {
                              const id = String(opt.id || '');
                              if (!id) return;
                              commitPriceDraft(idx, id);
                            }}
                            inputProps={{ step: 0.01, min: 0 }}
                          />
                          <TextField
                            label="Title"
                            value={opt.title}
                            onChange={(e) => updateOption(idx, { title: e.target.value })}
                          />
                          <TextField
                            label="Badge (optional)"
                            value={opt.badge || ''}
                            onChange={(e) => updateOption(idx, { badge: e.target.value })}
                          />
                        </Box>

                        <Card sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                          <CardContent sx={{ p: 2 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography fontWeight={900} noWrap>
                                  Action
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {(opt.action?.label || 'Not set') + (opt.action?.duration_days ? ` • ${opt.action.duration_days} Tage` : '')}
                                </Typography>
                                {opt.action?.details ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {opt.action.details}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Button variant="outlined" onClick={() => openActionDialog(idx)}>
                                Configure
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>

                        <TextField
                          label="Description"
                          value={opt.description}
                          onChange={(e) => updateOption(idx, { description: e.target.value })}
                          multiline
                          minRows={3}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                          <FormControlLabel
                            control={<Switch checked={opt.enabled !== false} onChange={(e) => updateOption(idx, { enabled: e.target.checked })} />}
                            label="Enabled"
                          />
                          <FormControlLabel
                            control={<Switch checked={!!opt.recommended} onChange={(e) => updateOption(idx, { recommended: e.target.checked })} />}
                            label="Recommended"
                          />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={actionDialogOpen} onClose={closeActionDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Choose action</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <FormControl fullWidth>
              <InputLabel id="boost-action-type-label">Action</InputLabel>
              <Select
                labelId="boost-action-type-label"
                label="Action"
                value={actionDraft?.type || 'visibility_boost'}
                onChange={(e) => {
                  const nextType = e.target.value as NonNullable<SubPayOption['action']>['type'];
                  const preset = ACTION_PRESETS.find((p) => p.type === nextType);
                  if (preset) {
                    setActionDraft({ type: preset.type, label: preset.label, details: preset.details, duration_days: preset.duration_days });
                  } else {
                    setActionDraft((prev) => ({ ...(prev || { type: nextType }), type: nextType }));
                  }
                }}
              >
                {ACTION_PRESETS.map((p) => (
                  <MenuItem key={p.type} value={p.type}>
                    {p.label}
                  </MenuItem>
                ))}
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Label"
              value={actionDraft?.label || ''}
              onChange={(e) => setActionDraft((prev) => ({ ...(prev || { type: 'custom' }), label: e.target.value }))}
              placeholder="z.B. Highlight"
            />

            <TextField
              label="Details"
              value={actionDraft?.details || ''}
              onChange={(e) => setActionDraft((prev) => ({ ...(prev || { type: 'custom' }), details: e.target.value }))}
              placeholder="Short explanation shown to the user"
              multiline
              minRows={3}
            />

            <TextField
              label="Duration (days)"
              type="number"
              value={typeof actionDraft?.duration_days === 'number' ? actionDraft.duration_days : ''}
              onChange={(e) => {
                const raw = String(e.target.value);
                if (raw.trim() === '') {
                  setActionDraft((prev) => ({ ...(prev || { type: 'custom' }), duration_days: undefined }));
                  return;
                }
                const parsed = Number(raw);
                setActionDraft((prev) => ({ ...(prev || { type: 'custom' }), duration_days: Number.isFinite(parsed) ? parsed : undefined }));
              }}
              inputProps={{ min: 0, step: 1 }}
              helperText="Leave empty for one-time / not duration based"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} variant="outlined">
            Cancel
          </Button>
          <Button onClick={saveActionDialog} variant="contained">
            Save action
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoostVisibilityTab;
